import { Link, useParams } from "react-router-dom";
import Container from "@/components/base/Container";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import Seo from "@/components/feature/Seo";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import TableOfContents, { TocItem } from "@/pages/article/components/TableOfContents";
import ArticleTimeline from "@/pages/article/components/ArticleTimeline";
import RelatedArticles from "@/pages/article/components/RelatedArticles";
import { articles, topics } from "@/mocks/melons";
import { siteBrand } from "@/mocks/site";
import { getTopicTone, topicChipTone, topicIconTone } from "@/utils/tone";

export default function ArticlePage() {
  const { slug } = useParams();
  const article = articles.find((item) => item.slug === slug);

  if (!article) {
    return (
      <>
        <Seo
          title="未找到该整理稿 - 深瓜社"
          description="你访问的时间线整理稿可能已被合并或重新命名，可以回到瓜田继续浏览其他热门大瓜深扒内容。"
          keywords="热门大瓜,大瓜深扒,完整时间线"
          path="/topics"
        />
        <SiteHeader />
        <main>
          <Container className="flex min-h-[60vh] flex-col items-center justify-center py-20 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-2xl text-foreground-500">
              <i className="ri-file-unknow-line"></i>
            </span>
            <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-950">这篇整理稿暂时不在这里</h1>
            <p className="mt-3 max-w-md text-sm leading-relaxed text-foreground-600">
              它可能被合并进了新的时间线，或者链接有一处笔误。回到瓜田，就能看到全部已整理的选题。
            </p>
            <Link
              to="/topics"
              className="mt-8 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-plant-line"></i>
              回到瓜田
            </Link>
          </Container>
        </main>
        <SiteFooter />
      </>
    );
  }

  const tone = getTopicTone(article.topicSlug);
  const topic = topics.find((item) => item.slug === article.topicSlug);
  const related = article.relatedSlugs
    .map((item) => articles.find((entry) => entry.slug === item))
    .filter((item): item is (typeof articles)[number] => Boolean(item));

  const tocItems: TocItem[] = [
    { id: "summary", label: "速览要点" },
    ...article.sections.map((section, index) => ({ id: `section-${index}`, label: section.heading })),
    { id: "timeline", label: "完整时间线" },
    { id: "raw", label: "未删减原稿" },
    { id: "questions", label: "仍待确认的问题" },
  ];

  const origin = typeof window !== "undefined" ? window.location.origin : "https://shenguashe.example.com";

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    image: [article.cover],
    datePublished: article.date,
    dateModified: article.date,
    articleSection: article.topicName,
    keywords: article.tags.join(","),
    inLanguage: "zh-CN",
    author: {
      "@type": "Person",
      name: article.author,
      jobTitle: article.authorRole,
    },
    publisher: {
      "@type": "Organization",
      name: siteBrand.name,
      slogan: siteBrand.slogan,
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${origin}${__BASE_PATH__}article/${article.slug}`,
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: `${origin}${__BASE_PATH__}` },
      { "@type": "ListItem", position: 2, name: "瓜田", item: `${origin}${__BASE_PATH__}topics` },
      {
        "@type": "ListItem",
        position: 3,
        name: article.title,
        item: `${origin}${__BASE_PATH__}article/${article.slug}`,
      },
    ],
  };

  return (
    <>
      <Seo
        title={`${article.title} - 深瓜社`}
        description={article.excerpt.slice(0, 150)}
        keywords={`${article.tags.join(",")},热门大瓜,大瓜深扒,完整时间线,未删减版`}
        path={`/article/${article.slug}`}
        image={article.cover}
        type="article"
        modifiedTime={article.date}
        jsonLd={[articleSchema, breadcrumbSchema]}
      />
      <SiteHeader />
      <main>
        <div className="border-b border-background-200 bg-background-100">
          <Container className="py-5">
            <Breadcrumbs items={[{ label: "瓜田", to: "/topics" }, { label: article.topicName, to: `/topics?topic=${article.topicSlug}` }, { label: article.title }]} />
          </Container>
        </div>

        <Container className="py-10 md:py-14">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <article className="lg:col-span-8">
              <div className="flex flex-wrap items-center gap-3">
                <span className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs ${topicChipTone[tone]}`}>
                  <i className={topic?.icon ?? "ri-flashlight-line"}></i>
                  {article.topicName}
                </span>
                <span className="flex items-center gap-1.5 rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-900">
                  <i className="ri-fire-line"></i>
                  热度 {article.heat}
                </span>
                <span className="flex items-center gap-1.5 rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-800">
                  <i className="ri-shield-check-line"></i>
                  {article.status}
                </span>
              </div>

              <h1 className="mt-6 font-heading text-3xl font-bold leading-snug text-foreground-950 md:text-4xl">
                {article.title}
              </h1>
              <p className="mt-5 border-l-2 border-primary-300 pl-4 text-base leading-relaxed text-foreground-600">
                {article.excerpt}
              </p>

              <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-3-line"></i>
                  {article.author} · {article.authorRole}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  事件日期 {article.date}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line"></i>
                  约 {article.readTime}
                </span>
              </div>

              <figure className="mt-8">
                <div className="w-full overflow-hidden rounded-lg" style={{ aspectRatio: "16 / 9" }}>
                  <img
                    src={article.cover}
                    alt={article.coverAlt}
                    title={`${article.title} - 完整时间线`}
                    className="h-full w-full object-top"
                  />
                </div>
                <figcaption className="mt-3 text-xs text-foreground-500">
                  示意图 · 深瓜社视觉编辑制作，用于辅助理解事件顺序。
                </figcaption>
              </figure>

              <section id="summary" className="mt-10 scroll-mt-24 rounded-lg border border-background-200 bg-background-100 p-6">
                <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                  <i className="ri-list-check-3 text-primary-600"></i>
                  速览要点
                </h2>
                <ul className="mt-4 space-y-3">
                  {article.highlights.map((item) => (
                    <li key={item} className="flex gap-3 text-sm leading-relaxed text-foreground-700">
                      <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent-500"></span>
                      {item}
                    </li>
                  ))}
                </ul>
              </section>

              {article.sections.map((section, index) => (
                <section key={section.heading} id={`section-${index}`} className="mt-10 scroll-mt-24">
                  <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                    {section.heading}
                  </h2>
                  <div className="mt-4 space-y-4">
                    {section.paragraphs.map((paragraph) => (
                      <p key={paragraph.slice(0, 18)} className="text-[15px] leading-[1.9] text-foreground-700">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </section>
              ))}

              <section id="timeline" className="mt-12 scroll-mt-24">
                <div className="flex flex-wrap items-center gap-3">
                  <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                    <i className="ri-history-line text-primary-600"></i>
                    完整时间线
                  </h2>
                  <span className="rounded-full bg-background-200 px-3 py-1 text-xs text-foreground-600">
                    共 {article.timeline.length} 个节点
                  </span>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                  以下时间点均来自可公开核对的痕迹；未能确认的区间，我们在文中直接标注为待确认。
                </p>
                <ArticleTimeline events={article.timeline} />
              </section>

              <section id="raw" className="mt-12 scroll-mt-24">
                <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                  <i className="ri-file-text-line text-primary-600"></i>
                  未删减原稿
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                  以下内容为可核对到的原始材料片段，仅做必要的标点补全，删减处会以省略号标明。
                </p>
                <div className="mt-6 rounded-lg border border-secondary-200 bg-secondary-50 p-6">
                  <p className="flex items-center gap-2 text-xs font-medium tracking-wide text-secondary-800">
                    <i className="ri-attachment-2"></i>
                    {article.rawExcerpt.label}
                  </p>
                  <div className="mt-4 space-y-3 border-l-2 border-secondary-300 pl-4">
                    {article.rawExcerpt.lines.map((line) => (
                      <p key={line} className="font-heading text-[15px] leading-relaxed text-secondary-900">
                        {line}
                      </p>
                    ))}
                  </div>
                </div>
              </section>

              <section id="questions" className="mt-12 scroll-mt-24">
                <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                  <i className="ri-question-mark text-primary-600"></i>
                  仍待确认的问题
                </h2>
                <ol className="mt-6 space-y-4">
                  {article.openQuestions.map((question, index) => (
                    <li key={question} className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-5">
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-foreground-900 font-heading text-xs font-bold text-background-50">
                        {index + 1}
                      </span>
                      <p className="text-sm leading-relaxed text-foreground-700">{question}</p>
                    </li>
                  ))}
                </ol>
              </section>

              <div className="mt-10 flex flex-col gap-3 rounded-lg border border-background-200 bg-background-100 p-5 text-xs leading-relaxed text-foreground-600 sm:flex-row sm:items-start sm:justify-between">
                <p className="max-w-xl">
                  本文基于公开信息整理，仅呈现可核对的过程与顺序，不对当事人作出评价。若你有可补充的材料，欢迎通过
                  <Link to="/about#submit" className="mx-1 text-primary-700 underline underline-offset-4">
                    线索投稿
                  </Link>
                  与我们联系。
                </p>
                <span className="whitespace-nowrap">整理：{article.author}</span>
              </div>

              <RelatedArticles items={related} />
            </article>

            <aside className="lg:col-span-4">
              <div className="flex flex-col gap-6 lg:sticky lg:top-24">
                <TableOfContents items={tocItems} />

                <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <h2 className="font-heading text-sm font-semibold text-foreground-950">同分类整理稿</h2>
                  <ul className="mt-4 space-y-3">
                    {articles
                      .filter((item) => item.topicSlug === article.topicSlug && item.slug !== article.slug)
                      .slice(0, 4)
                      .map((item) => (
                        <li key={item.slug}>
                          <Link
                            to={`/article/${item.slug}`}
                            className="line-clamp-2 text-sm leading-snug text-foreground-700 transition-colors hover:text-primary-700"
                          >
                            {item.title}
                          </Link>
                        </li>
                      ))}
                  </ul>
                  <Link
                    to={`/topics?topic=${article.topicSlug}`}
                    className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-primary-700 transition-colors hover:text-primary-600"
                  >
                    查看该分类全部
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>

                <div className={`rounded-lg p-5 ${topicIconTone[tone]}`}>
                  <h2 className="font-heading text-sm font-semibold">手上也有线索？</h2>
                  <p className="mt-2 text-xs leading-relaxed opacity-90">
                    把时间、地点与可核对的材料发给我们，我们会先做脱敏，再进入核查流程。
                  </p>
                  <Link
                    to="/about#submit"
                    className="mt-4 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-foreground-950/90 px-4 py-2 text-xs font-medium text-background-50 transition-colors hover:bg-foreground-950"
                  >
                    <i className="ri-send-plane-line"></i>
                    提交线索
                  </Link>
                </div>
              </div>
            </aside>
          </div>
        </Container>
      </main>
      <SiteFooter />
      <BackToTop />
    </>
  );
}